import { useBizQuery } from '../../../../../FrontEndReact/src/Core/Hooks/useBizQuery';
import { get, post } from '../../../../../FrontEndReact/src/Core/Services/common';
import { DashboardPeriod, getDateRangeForPeriod } from '../../../../../FrontEndReact/src/Core/Types/admin/dashboard';
import { formatToSimpleDateString } from '../../../../../FrontEndReact/src/Core/Utils/dateUtils';

const apiPath = 'api/laxhjalpenstats/';
const QUERYKEY_LH_USER_PARTICIPATION = 'LH_USER_PARTICIPATION';

export type LhUserParticipationStats = {
	userId: number;
	userName: string;
	eventCount: number;
	attendedCount: number;
	approvedAbsenceCount: number;
	notApprovedAbsenceCount: number;
};

export type LhUserParticipationStatsResponse = {
	userParticipation: LhUserParticipationStats[];
	uniqueEvents: number;
	uniqueEventsWApprovedAbsence: number;
	uniqueEventsWNotApprovedAbsence: number;
	lastUpdated?: string | null;
};

export type LhSimpleUser = {
	id: number;
	userName: string;
	firstName: string;
	lastName: string;
	name?: string;
	email?: string;
	photo?: string;
	mobile?: string;
	isDeleted?: boolean;
};

export type LhStudentParticipationStats = LhUserParticipationStats & {
	unitRank: number;
	unit?: {
		id: number;
		name: string;
	};
	user?: LhSimpleUser;
};

export type LhGuardianParticipationStatsResponse = LhStudentParticipationStats[];

export const useGetUserParticipationStats = (unitId: number | undefined, period: DashboardPeriod) => {
	const { from, to } = getDateRangeForPeriod(period);
	const { data, statusState } = useBizQuery([QUERYKEY_LH_USER_PARTICIPATION, unitId ?? -1, period], () =>
		getUserParticipationStats(unitId, from, to)
	);

	return {
		data: data?.userParticipation ?? [],
		uniqueEvents: data?.uniqueEvents ?? 0,
		uniqueEventsWApprovedAbsence: data?.uniqueEventsWApprovedAbsence ?? 0,
		uniqueEventsWNotApprovedAbsence: data?.uniqueEventsWNotApprovedAbsence ?? 0,
		lastUpdated: data?.lastUpdated ?? null,
		from,
		to,
		statusState,
	};
};

export const useGetLoggedInUserParticipationStats = (userId: number | undefined, enabled: boolean) => {
	const shouldFetch = enabled && userId !== undefined;
	const { data, statusState } = useBizQuery(
		[QUERYKEY_LH_USER_PARTICIPATION, 'logged-in-user', userId],
		() => getLoggedInUserParticipationStats(),
		shouldFetch
	);

	return {
		data: shouldFetch ? data ?? null : null,
		statusState,
	};
};

export const useGetRelatedStudentParticipationStatsToLoggedInGuardian = (userId: number | undefined, enabled: boolean) => {
	const shouldFetch = enabled && userId !== undefined;
	const { data, statusState } = useBizQuery(
		[QUERYKEY_LH_USER_PARTICIPATION, 'guardian-related-students', userId],
		() => getRelatedStudentParticipationStatsToLoggedInGuardian(),
		shouldFetch
	);

	return {
		data: shouldFetch ? data ?? [] : [],
		statusState,
	};
};

async function getUserParticipationStats(unitId: number | undefined, from: Date, to: Date) {
	const endpoint = `${apiPath}GetUserParticipationStats?unitId=${unitId ?? -1}&from=${formatToSimpleDateString(from)}&to=${formatToSimpleDateString(to)}`;
	return post<LhUserParticipationStatsResponse>(endpoint);
}

async function getLoggedInUserParticipationStats() {
	return get<LhStudentParticipationStats>(`${apiPath}GetLoggedInUserParticipationStats`);
}

async function getRelatedStudentParticipationStatsToLoggedInGuardian() {
	return get<LhGuardianParticipationStatsResponse>(`${apiPath}GetRelatedStudentParticipationStatsToLoggedInGuardian`);
}
